import {useRef} from 'react'
import {zodResolver} from '@hookform/resolvers/zod'
import {formatDate} from '@/dayjs'
import {PatientExtended} from '@/features/patients/types'
import {FormProvider, useForm, useWatch} from 'react-hook-form'
import {useToggle} from '@/hooks/useToggle'
import {useNavigate, useOutletContext} from 'react-router-dom'
import {Divider} from '@/components/ui/divider/Divider'
import {PageContent} from '@/components/ui/page-atoms/PageAtoms'
import {ScrollArea} from '@/components/ui/scroll-area/ScrollArea'
import {Scrollbar} from '@/components/ui/scrollbar/Scrollbar'
import {BannerAlert} from '@/components/commons/banner-alert/BannerAlert'
import {UnsavedBlocker} from '@/components/commons/unsaved-blocker/UnsavedBlocker'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useCreateMedicalReport} from '@/features/medical-reports/queries/useCreateMedicalReport'
import {MedicalReportValidationSchema} from '@/features/medical-reports/formSchemas'
import {useMedicalReportFormStatus} from '@/features/medical-reports/hooks/useMedicalReportFormStatus'
import {HttpMedicalReportPayload} from '@/features/medical-reports/http/medicalReports.http'
import {getAttributesFromValues, remapMedicalReportAttributesToPayload} from '@/features/medical-reports/utils'
import {MedicalReportFormHeader} from '@/features/medical-reports/components/medical-report-form-header/MedicalReportFormHeader'
import {MedicalReportConfirmationModal} from '@/features/medical-reports/components/medical-report-confirmation-modal/MedicalReportConfirmationModal'
import {MedicalReportFields} from '@/features/medical-reports/components/medical-report-fields/MedicalReportFields'
import {errorHandler, ObjectKeys} from '@/utilities/helpers'
import {MedicalReportTypeGroupKeyName, MedicalReportTypeKeyName} from '@/types'
import {ProjectExtended} from '@/features/projects/types'
import {Access} from '@/features/accesses/types'
import {ProjectPlannedAccess} from '@/features/planned-accesses/types'
import {useTranslation} from '@/translations/i18n'

interface CreateMedicalReportFormProps {
    /**
     * Initial project ID to pre-select in the form
     */
    initialProject?: ProjectExtended
    /**
     * Initial medical report type information to pre-select in the form
     */
    initialMedicalReportType?: {
        id: string
        keyName?: MedicalReportTypeKeyName
        name?: string
        groupKeyName?: MedicalReportTypeGroupKeyName
        groupName?: string
    }
    /**
     * Initial access information to pre-select in the form
     */
    initialAccesses?: Access[]
    /**
     * Initial planned accesses information to pre-select in the form
     */
    initialPlannedAccesses?: ProjectPlannedAccess
    /**
     * Initial date to pre-select in the form
     */
    initialDate?: Date
}

export const CreateMedicalReportForm = ({
    initialProject,
    initialMedicalReportType,
    initialAccesses,
    initialPlannedAccesses,
    initialDate
}: CreateMedicalReportFormProps = {}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()

    const {isActive: isConfirmationModalVisible, on: showConfirmationModal, off: dismissConfirmationModal} = useToggle()
    const {isActive: isErrorBannerVisible, on: showErrorBanner, off: dismissErrorBanner} = useToggle()

    const viewportRef = useRef<HTMLDivElement>(null)
    const errorBannerRef = useRef<HTMLDivElement>(null)

    const {patient} = useOutletContext<{patient: PatientExtended}>()

    const form = useForm<MedicalReportValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(MedicalReportValidationSchema),
        defaultValues: {
            reportDate: initialDate || new Date(),
            medicalRecordId: patient.medicalRecord?.id,
            project: initialProject,
            reportTypeKeyName: initialMedicalReportType?.keyName,
            ...(initialMedicalReportType?.keyName == 'continuous_project_extension_request' && {
                isMixedProject: initialProject?.sector == 'mixed'
            }),
            reportType: {
                id: initialMedicalReportType?.id,
                name: initialMedicalReportType?.name,
                keyName: initialMedicalReportType?.keyName,
                groupKeyName: initialMedicalReportType?.groupKeyName,
                groupName: initialMedicalReportType?.groupName
            },
            status: 'draft',
            isReportAccessDetatched: false,
            accesses: initialAccesses || undefined,
            noProject: false,
            ...((initialMedicalReportType?.keyName == 'start_cyclical_project' ||
                initialMedicalReportType?.keyName == 'start_continuous_project') && {
                treatmentsPlan:
                    initialMedicalReportType?.keyName == 'start_cyclical_project' ||
                    initialMedicalReportType?.keyName == 'start_continuous_project'
                        ? initialPlannedAccesses?.treatmentPlan || ''
                        : undefined,
                objectives:
                    initialMedicalReportType?.keyName == 'start_cyclical_project' ||
                    initialMedicalReportType?.keyName == 'start_continuous_project'
                        ? initialPlannedAccesses?.targets || ''
                        : undefined
            })
        }
    })

    const projectId = useWatch({control: form.control, name: 'project.id' as const})

    const createMedicalReportMutation = useCreateMedicalReport({
        onSuccess: async () => {
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORTS]})
            await queryClient.cancelQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS_BY_PATIENT, patient.id]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PATIENT_MEDICAL_RECORD]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.USERS_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORT_TYPES_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.MEDICAL_REPORT_DATES_BY_PATIENT]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECTS]})
            await queryClient.invalidateQueries({queryKey: [QUERY_KEYS.PROJECT, projectId]})

            navigate(-1)
        },
        onError: errors => {
            dismissConfirmationModal()
            if (errors?.response?.data.errors) {
                showErrorBanner()
                if (viewportRef.current) {
                    viewportRef.current.scrollTo({
                        top: 0,
                        behavior: 'smooth'
                    })
                }
            } else {
                errorHandler(errors)
            }
        }
    })

    const formStatus = useMedicalReportFormStatus({
        form,
        mutation: createMedicalReportMutation
    })

    // Function to handle the creation of a medical report
    const createMedicalReport = (formValues: MedicalReportValidationSchema) => {
        // Extract and map the form values into the required payload structure
        const attributesFormValues = getAttributesFromValues(formValues)
        const attributes = remapMedicalReportAttributesToPayload(attributesFormValues)

        // Execute the mutation with the parsed payload
        createMedicalReportMutation.mutate(
            HttpMedicalReportPayload.parse({
                medicalRecordReportTypeId: formValues.reportType.id,
                medicalRecordId: formValues.medicalRecordId,
                projectId: formValues.noProject ? null : formValues.project?.id,
                status: formValues.status,
                date: formatDate(formValues.reportDate, 'YYYY-MM-DD'),
                endDate: formValues.endDate ? formatDate(formValues.endDate, 'YYYY-MM-DD') : undefined,
                accessIds: formValues.accesses?.map(access => access.id),
                attributes
            })
        )
    }

    // Main function for handling validation and confirmation modal
    const handleSubmitForm = form.handleSubmit(
        // Success callback when the form validation passes
        () => {
            if (isErrorBannerVisible) {
                dismissErrorBanner()
            }
            showConfirmationModal()
        },
        // Error callback when the form validation fails
        errors => {
            console.error(errors)
            showErrorBanner()
            if (viewportRef.current) {
                viewportRef.current.scrollTo({
                    top: 0,
                    behavior: 'smooth'
                })
            }
        }
    )

    // Function to handle the form submission logic based on the action (draft save or submit)
    const handleSubmitControl = ({isDraftSaving}: {isDraftSaving: boolean}) => {
        if (isDraftSaving) {
            // If saving as draft, directly get form values and create the report
            const formValues = form.getValues()

            if (!formValues.reportDate) {
                form.setError('reportDate', {message: t('errors:required')})
                return
            }

            createMedicalReport(formValues)
        } else {
            // Otherwise, trigger the full form submission process
            handleSubmitForm()
        }
    }

    // Function to handle confirmation of form submission
    const handleConfirmSubmit = form.handleSubmit((data: MedicalReportValidationSchema) => createMedicalReport(data))

    return (
        <FormProvider {...form}>
            <MedicalReportFormHeader
                isFormBlocked={formStatus.isDisabled}
                onSubmit={handleSubmitControl}
                isPendingSubmit={formStatus.isPendingSubmit}
                isPendingDraftSaving={formStatus.isPendingDraftSaving}
            />
            <Divider direction="horizontal" />
            <PageContent padding={0}>
                <ScrollArea style={{height: '100%'}} scrollbar={<Scrollbar />} viewportProps={{ref: viewportRef}}>
                    {isErrorBannerVisible && (
                        <>
                            <BannerAlert ref={errorBannerRef} onClose={dismissErrorBanner} />
                            <Divider direction="horizontal" />
                        </>
                    )}
                    <UnsavedBlocker
                        isDirty={
                            form.formState.isDirty &&
                            createMedicalReportMutation.status == 'idle' &&
                            ObjectKeys(form.formState.dirtyFields).length > 0
                        }
                    >
                        <MedicalReportFields view="create" isTypeProjectCompliant={!formStatus.isDisabled} />
                        {isConfirmationModalVisible && (
                            <MedicalReportConfirmationModal
                                onConfirm={handleConfirmSubmit}
                                onClose={dismissConfirmationModal}
                                isLoading={createMedicalReportMutation.isPending}
                                patientFullName={patient.fullName}
                            />
                        )}
                    </UnsavedBlocker>
                </ScrollArea>
            </PageContent>
        </FormProvider>
    )
}
